/**
 * The single configured TanStack QueryClient. Server state lives ONLY here (never in Zustand).
 *
 * Defaults tuned for the local-first box (ENGINEERING_STANDARDS): generous staleTime, keep
 * previous data across key changes, no refetch-on-focus storms, and a retry policy that never
 * retries auth/forbidden/validation (those are terminal for the request).
 */
import { QueryClient } from "@tanstack/react-query";
import { ApiError } from "./types";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        if (error instanceof ApiError) {
          // Auth, forbidden, validation, not-found, not-implemented are terminal.
          if (error.isUnauthenticated || error.isForbidden) return false;
          if ([422, 404, 501].includes(error.httpStatus)) return false;
        }
        return failureCount < 2;
      },
    },
    mutations: {
      retry: false,
    },
  },
});

/**
 * Hierarchical query-key factory, scoped by laboratoireId (multi-tenant: a lab switch invalidates
 * the scope so one tenant's cache never bleeds into another). Feature modules extend their own
 * sub-factories under `qk.feature(...)`.
 *
 *   qk.scoped(labId, "reception", "dossier", { page: 1 })
 *     -> ["lab", labId, "reception", "dossier", { page: 1 }]
 */
export const qk = {
  scoped: (laboratoireId: string | number | null, ...parts: ReadonlyArray<unknown>) =>
    ["lab", laboratoireId ?? "_", ...parts] as const,
  /** Invalidate every query for a lab (call on lab switch). */
  labRoot: (laboratoireId: string | number | null) => ["lab", laboratoireId ?? "_"] as const,
};
